// src/com/auto_com_veh_info/AutoComVehInfo.js
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import { Button, IconButton, CircularProgress, FormControlLabel, Checkbox } from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import CloseIcon from '@mui/icons-material/Close'; // com_info.use_yn==='N'
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import SearchBox from '../../common/SearchBox';
import {ContentConainer, ContentHeader, ContentBody, ControlBox, ToolBox} from '../../common/TopViewControl';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import { AppObject, AppStyle, AppWord } from '../../value/AppConst';
import { authTableTreeRepo, autoComVehInfoListRepo, comInfoListRepo } from '../../value/AppModel';
import { LIST_COM_INFO, makeComInfoTree } from '../com_info/ComInfoGql';
import {
    LIST_AUTO_COM_VEH_INFO, GET_AUTO_COM_VEH_INFO,
    ADD_AUTO_COM_VEH_INFO, EDIT_AUTO_COM_VEH_INFO, REMOVE_AUTO_COM_VEH_INFO
} from './AutoComVehInfoGql';
import { AutoComVehInfoDictionary } from './AutoComVehInfoDict';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import FlexyTable from '../../common/flexytable/FlexyTable';
import AutoComVehInfoInput from './AutoComVehInfoInput';

const ColumnContainer = styled(Box)({
    display: 'flex', flexDirection: 'column', boxSizing: 'border-box', // height: '100%', border: '1px solid red'
});

const ComNameBox = styled(Box)({
    display:'flex', alignItems:'center', fontWeight: 'bold', paddingTop:10, marginRight:40
});

const DcTypeLine = styled('div')({
    display: 'flex', alignItems: 'center', backgroundColor: '#eee'
});
const DcTypeLabel = styled('span')({
    fontSize: '0.9rem', color: AppStyle.PrimaryRGB, width: 100, display: 'inline-block',
    padding: 5, fontWeight:'bold' // border: AppStyle.BorderCCC, borderRadius: 5, textAlign: 'center',
});

const DcGroup = styled('div')({
    marginLeft: 32, fontSize: '0.85rem',
});
const DcLabelLine = styled('div')({
    display: 'flex', alignItems: 'center',
});
const DcLabel = styled('span')({
    padding: 5, display: 'inline-block', // borderBottom: AppStyle.BorderCCC,
});

const ComListBox = styled('div')({
    marginLeft: 32
});

/**
 * (AUTO_COM_VEH_INFO) 정보 View Controller
 * 운송사 차종 등록/DC별 운송사 배차 차종
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-10-14 16:28:24 using DbdbTap
 */
export default function AutoComVehInfo({
    viewServiceTags // for switching view
}) {
    const [showAllDc, setShowAllDc] = useState(false);
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const comInfoListData = useReactiveVar(comInfoListRepo);
    const [comInfoTree, setComInfoTree] = useState([]); // from comInfoListData. dcTypeCd => dcCd => comId
    // const [viewerState, setViewerState] = useState(AppObject.EmptyViewerState);
    const [editorState, setEditorState] = useState(AppObject.EmptyViewerState);
    const autoComVehInfoListData = useReactiveVar(autoComVehInfoListRepo);
    const [comSelected, setComSelected] = useState({dcCd:'', comId:''});
    const [searchText, setSearchText] = useState('');
    const openConfirmDialog = useConfirmDialog();
    const checkAuth = useAuthorizationCheck();
    const addAlert = useAlertSetter();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const lastReloadRef = useRef(0);
    const isEditingRef = useRef(false); // 플래그 생성

    // ##### Call GraphQL to get List #####
    const [getComInfoList, responseComList] = useLazyQuery(LIST_COM_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetComList(data, option)},
        onError: (error) => addAlert({title: '운송사 목록 조회 오류', message: [error.message]})
    });

    const [getAutoComVehInfoList, responseList] = useLazyQuery(LIST_AUTO_COM_VEH_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: AutoComVehInfoDictionary.errorTitle.Get, message: [error.message]})
    });
    const [getAutoComVehInfoItem, responseItem] = useLazyQuery(GET_AUTO_COM_VEH_INFO, {
		...AppObject.NoCachedFetch,
		onCompleted: (data, option) => {onCompleteGetItem(data, option)},
		onError: (error) => addAlert({title: AutoComVehInfoDictionary.errorTitle.Get, message: [error.message]})
	});
    
    // ##### GraphQL Mutation. 'onError' for prevent from printing error at console (Uncaught exception) ###
    const [addAutoComVehInfo, responseAdd] = useMutation( ADD_AUTO_COM_VEH_INFO, {
		onCompleted: (data, option) => onCompleteAdd(data, option), 
		onError: (error) => addAlert({title: AutoComVehInfoDictionary.errorTitle.Add, message: [error.message]})
	} );
    const [editAutoComVehInfo, responseEdit] = useMutation( EDIT_AUTO_COM_VEH_INFO, {
		onCompleted: (data, option) => onCompleteEdit(data, option),
		onError: (error) => addAlert({title: AutoComVehInfoDictionary.errorTitle.Edit, message: [error.message]})
	} );
    const [removeAutoComVehInfo, responseRemove] = useMutation( REMOVE_AUTO_COM_VEH_INFO, {
		onCompleted: (data, option) => onCompleteRemove(data, option),
		onError: (error) => addAlert({title: AutoComVehInfoDictionary.errorTitle.Remove, message: [error.message]})
	});

    useEffect(() => {
        const diff = new Date().getTime()/1000 - comInfoListData.when;
        if(diff > 300 ) // || comInfoListData.records.length === 0
            getComInfoList();
    }, []);

    useEffect(()=>{
        if(comInfoListData.records.length > 0) {
            const tree = makeComInfoTree(comInfoListData.records);
            setComInfoTree(tree);
        }
    }, [comInfoListData]);

    const getAutoComVehInfoListNow = (comInfoItem) => {

        const comInfo
        = comInfoItem ? comInfoItem
        : autoComVehInfoListData.comInfo.dcCd && autoComVehInfoListData.comInfo.comId
            ? autoComVehInfoListData.comInfo
            : comSelected.dcCd && comSelected.comId
                ? comSelected
                : null;
        if(!comInfo) return;

        const param = {variables:{dcCd: comInfo.dcCd, comId: comInfo.comId}};
        getAutoComVehInfoList(param);
        lastReloadRef.current = new Date().getTime()/1000;
    };


    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetComList = (data, option) => {
        if(data.comInfoList) {
            comInfoListRepo({when:new Date().getTime()/1000, records:data.comInfoList});
        }
    };

    const onCompleteGetList = (data, clientOption) => {
        if(data.autoComVehInfoList) {
            // unique key를 추가한다.
            const list = data.autoComVehInfoList.map((item, idx) => {
                return {...item, key: AutoComVehInfoDictionary.tableProps.getKey(item)};
            });
            autoComVehInfoListRepo({
                when:new Date().getTime()/1000,
                records:list,
                comInfo: {...comSelected} // comSelected는 데이터 수신 전에 onClickComButton에서 설정됨.
            });

            if(list.length === 0) {
                enqueueSnackbar(`${comSelected.comName} 운송사에 등록된 차종이 없습니다.`, {variant:'info', autoHideDuration:2000});
            }
        }
    };

    const onCompleteGetItem = (data, option) => {
        if(data.autoComVehInfoItem) {
            setEditorState({open:true, item:data.autoComVehInfoItem});
        }
        else addAlert({title:AutoComVehInfoDictionary.errorTitle.Get, message:['선택한 차종을 찾을 수 없습니다.']});
    };

    const onCompleteAdd = (data, clientOption) => {
        if(data.autoComVehInfoAdd.ok) {
            setEditorState(AppObject.EmptyViewerState);
            getAutoComVehInfoListNow();
        }
        else addAlert({title:AutoComVehInfoDictionary.errorTitle.Add, message:[data.autoComVehInfoAdd.message]});
    };

    const onCompleteEdit = (data, clientOption) => {
        if(data.autoComVehInfoEdit.ok) {
            setEditorState(AppObject.EmptyViewerState);
            getAutoComVehInfoListNow();
        }
        else addAlert({title:AutoComVehInfoDictionary.errorTitle.Edit, message:[data.autoComVehInfoEdit.message]});
    };

    const onCompleteRemove = (data, clientOption) => {
        if(data.autoComVehInfoRemove.ok) getAutoComVehInfoListNow();
        else addAlert({title:AutoComVehInfoDictionary.errorTitle.Remove, message:[data.autoComVehInfoRemove.message]});
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<
    // 좌측 트리에서 운송사를 클릭하면 해당 운송사에 등록된 차종 목록을 조회한다.
    const onClickComButton = (comInfo) => {
        setComSelected(comInfo);
        getAutoComVehInfoListNow(comInfo);
    };

    const onClickNotUsingIcon = (comInfo) => {
        addAlert({title:'사용 중지 상태', message:[`${comInfo.comName} 운송사는 사용 중지된 상태입니다.`]});
    };

    // 차종 목록을 새로고침한다.
    const onClickReload = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 60) {
            // Too short to reload
            enqueueSnackbar(AppWord.ReloadNotInOneMinute, {variant:'info', autoHideDuration:2000});
        }
        else {
            getAutoComVehInfoListNow();
        }
    };

    // 편집창에서 저장 버튼 클릭
    const onRequestSave = (item, isEdit) => {
        const mutateParam = {variables:{autoComVehInfo:item}};
        if(isEdit) editAutoComVehInfo(mutateParam);
        else addAutoComVehInfo(mutateParam);
    };

    // 차종 추가 버튼 클릭
    const onClickAdd = () => {
        setEditorState({open:true, item:null});
    };

    // 차종 수정 버튼 클릭
    const onRequestEdit = (item) => {
        isEditingRef.current = true;
        getAutoComVehInfoItem({variables:{dcCd:item.dcCd, comId:item.comId, vehTypeId:item.vehTypeId}});
    };

    // 차종 삭제 버튼 클릭
    const confirmRemove = async (item) => {
        const confirm = await openConfirmDialog({
            title: "운송사 차종 정보 삭제",
            content: [
                `운송사 차종(${item.vehTypeName})을 삭제하시겠습니까?`,
                '삭제 후 복구할 수 없습니다. 계속하시겠습니까?'
            ]
        });

        if(confirm)
            removeAutoComVehInfo({variables:{autoComVehInfo:{dcCd:item.dcCd, comId:item.comId, vehTypeId:item.vehTypeId}}});
    };

    const onChangeSearchText = (text) => {
        setSearchText(text);
    };

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.DELETE: confirmRemove(row); break;
            case AppObject.command.EDIT: onRequestEdit(row); break;
            default: return;
        }
    };

    const canAdd = checkAuth(authTableTree.tree.pr_auto_com_veh_info_add);
    const canEdit = checkAuth(authTableTree.tree.pr_auto_com_veh_info_edit);
    const canRemove = checkAuth(authTableTree.tree.pr_auto_com_veh_info_remove);
    const tools = [];

    if(canEdit)
        tools.push({id:AppObject.command.EDIT, icon:<EditIcon/>});

    if(canRemove)
        tools.push({id:AppObject.command.DELETE, icon:<DeleteForeverIcon/>, color: AppStyle.mui.warning});
    
    const renderComListBox = (dc) => {
        return(
            <ComListBox>
                {
                    dc.comList
                    .sort((a,b)=>a.comName.localeCompare(b.comName))
                    .map((com) => {
                        return(
                            <div key={com.comId}>
                                <Button
                                    variant={AppStyle.mui.text}
                                    onClick={()=>onClickComButton(com)}
                                    size={AppWord.SMALL}
                                    color={
                                        autoComVehInfoListData.comInfo.comId === com.comId
                                        ? AppStyle.mui.secondary
                                        : AppStyle.mui.primary
                                    }
                                >
                                    {com.comName}
                                </Button>
                                {
                                    com.useYn==='N' &&
                                    <IconButton
                                        title='사용 중지 상태'
                                        size={AppWord.SMALL}
                                        onClick={()=>onClickNotUsingIcon(com)}
                                    >
                                        <CloseIcon
                                            style={{
                                                fontSize:12,
                                                color: '#f77',
                                            }}
                                        />
                                    </IconButton>
                                }
                            </div>
                        );
                    })
                }
            </ComListBox>
        );
    }

    const renderComTree = () => {
        return(
            <Box
                style={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    overflow: 'auto', 
                }}
            >
                {
                    // 상온 타입만 또는 전체.
                    comInfoTree.filter((dcType)=>showAllDc || dcType.dcTypeCd === 'A')
                    .map((dcType, idx) => {
                        return (
                            <Box key={dcType.dcTypeCd}>
                                <DcTypeLine>
                                    <DcTypeLabel>{dcType.dcTypeName}</DcTypeLabel>
                                </DcTypeLine>
                                <div>
                                    {
                                        dcType.dcList
                                        .sort((a,b)=>a.dcName.localeCompare(b.dcName))
                                        .map((dc) => {
                                            return (
                                                <DcGroup key={dc.dcCd}>
                                                    <DcLabelLine>
                                                        <HomeWorkIcon style={{fontSize:12, color:'#777'}} />
                                                        <DcLabel>{dc.dcName}</DcLabel>
                                                    </DcLabelLine>
                                                    {renderComListBox(dc)}
                                                </DcGroup>
                                            )
                                        })
                                    }
                                </div>
                            </Box>
                        )
                    })
                }
            </Box>
        );
    };

    const loadingAny
        = responseList.loading
        || responseItem.loading
        || responseAdd.loading
        || responseEdit.loading
        || responseRemove.loading
        || responseComList.loading;

    return(
        <ContentConainer>
            {viewServiceTags}
            <ContentBody style={{display:'flex'}}>
                <ColumnContainer width={300} style={{paddingRight:10}}>
                    <ContentHeader>
                        <FormControlLabel
                            control={
                                <Checkbox checked={showAllDc}
                                    onChange={ (e)=>{ setShowAllDc(e.target.checked) } }
                                />
                            }
                            label="모든 DC 타입 보기"
                        />
                    </ContentHeader>
                    <Box style={{
                        flexGrow:1, position:'relative',
                        border: AppStyle.BorderCCC, boxSizing:'border-box',
                        borderRadius:5, padding:10,
                    }}
                    >
                        {renderComTree()}
                    </Box>
                </ColumnContainer>
                <ColumnContainer flexGrow={1}>
                    <ContentHeader>
                        <ComNameBox>
                            {
                                autoComVehInfoListData.comInfo.comId ?
                                <span>{autoComVehInfoListData.comInfo.comName}</span> :
                                <span>운송사를 선택하세요.</span>
                            }
                        </ComNameBox>
                        <ControlBox>
                            <SearchBox
                                value={searchText}
                                onChangeText={onChangeSearchText}
                                disabled={responseList.loading}
                            />
                        </ControlBox>
                        <ToolBox>
                            {
                                loadingAny ?
                                <CircularProgress size={20} />
                                :
                                <IconButton
                                    color={AppStyle.mui.inherit}
                                    onClick={onClickReload}
                                    disabled={
                                        !autoComVehInfoListData.comInfo.dcCd
                                        || !autoComVehInfoListData.comInfo.comId
                                    }
                                >
                                    <CachedIcon />
                                </IconButton>
                            }
                            {
                                canAdd &&
                                <IconButton
                                    color={AppStyle.mui.primary}
                                    style={{marginLeft:10}}
                                    onClick={onClickAdd}
                                    disabled={
                                        loadingAny
                                        || !autoComVehInfoListData.comInfo.comId
                                    }
                                >
                                    <NoteAddIcon />
                                </IconButton>
                            }
                        </ToolBox>
                    </ContentHeader>
                    <FlexyTable
                        name={AutoComVehInfoDictionary.tableProps.tableId}
                        tableProps={AutoComVehInfoDictionary.tableProps}
                        records={autoComVehInfoListData.records}
                        search={searchText}
                        tools={tools}
                        onClickTool={onClickToolOnRow}
                    />
                    <AutoComVehInfoInput
                        open={editorState.open}
                        item={editorState.item}
                        comInfo={autoComVehInfoListData.comInfo}
                        currentList={autoComVehInfoListData.records}
                        onClose={()=>setEditorState(AppObject.EmptyViewerState)}
                        responseSaving={editorState.item ? responseEdit : responseAdd}
                        onRequestSave={onRequestSave}
                    />
                </ColumnContainer>
            </ContentBody>
        </ContentConainer>
    );
}
